import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PhoneCall, RefreshCw, Search, X, Filter,
  AlertTriangle, Building2, ChevronLeft, ChevronRight, Eye
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ToastProvider';
import { cobranzaAPI, describirError } from '../../services/api';
import { formatoMoneda, formatoTelefono, limpiarNombreGrupo } from '../../utils/finanzas';
import RegistrarGestionModal from '../../components/RegistrarGestionModal';
import '../../styles/Pages.css';
import '../../styles/DataTable.css';
import '../../styles/FormModal.css';
import '../../styles/CobranzaTab.css';

const TRAMOS_ATRASO = [
  { value: '', label: 'Todos los tramos' },
  { value: '1-7', label: '1–7 días (Mora temprana)' },
  { value: '8-15', label: '8–15 días (Mora temprana)' },
  { value: '16-30', label: '16–30 días (Mora temprana)' },
  { value: '31-60', label: '31–60 días (Riesgo)' },
  { value: '61-90', label: '61–90 días (Riesgo)' },
  { value: '91+', label: '91+ días (Recuperación)' },
  { value: '0', label: '0 días (Al corriente / vence hoy)' }
];

const MODALIDADES = [
  { value: '', label: 'Todas las modalidades' },
  { value: 'INDIVIDUAL', label: 'Individual / Personal' },
  { value: 'GRUPAL', label: 'Grupal' },
  { value: 'COLOCADORA', label: 'Colocadora' }
];

const FRECUENCIAS = [
  { value: '', label: 'Todas las frecuencias' },
  { value: 'SEMANAL', label: 'Semanal' },
  { value: 'CATORCENAL', label: 'Catorcenal' },
  { value: 'QUINCENAL', label: 'Quincenal' },
  { value: 'MENSUAL', label: 'Mensual' }
];

const OPCIONES_ORDEN = [
  { value: 'prioridad_desc', label: 'Prioridad operativa' },
  { value: 'saldo_vencido_desc', label: 'Mayor saldo vencido' },
  { value: 'saldo_actual_desc', label: 'Mayor saldo actual' },
  { value: 'atraso_desc', label: 'Mayor atraso (días)' },
  { value: 'vencimiento_asc', label: 'Vencimiento más antiguo' },
  { value: 'cliente_asc', label: 'Cliente (A–Z)' },
  { value: 'folio_asc', label: 'Folio (Ascendente)' }
];

const QUICK_TABS = [
  { id: '', label: 'TODOS', key: 'todos' },
  { id: 'PREVENTIVA', label: 'MORA TEMPRANA', sub: '1–30 días', key: 'moraTemprana' },
  { id: 'RIESGO', label: 'RIESGO', sub: '31–90 días', key: 'riesgo' },
  { id: 'RECUPERACION', label: 'RECUPERACIÓN', sub: '91+ días', key: 'recuperacion' },
  { id: '0', label: 'AL CORRIENTE / VENCE HOY', sub: '0 días', key: 'alCorriente' }
];

const MESES_ABR = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

const fechaCorta = (iso) => {
  if (!iso) return '—';
  if (typeof iso === 'string') {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = match[1];
      const monthIdx = parseInt(match[2], 10) - 1;
      const day = match[3];
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${day} ${MESES_ABR[monthIdx]} ${year}`;
      }
    }
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).toUpperCase();
};

export default function Cobranza() {
  const navigate = useNavigate();
  const toast = useToast();
  const { reviewMode } = useAuth();

  const [resumen, setResumen] = useState(null);
  const [bucketSummary, setBucketSummary] = useState(null);
  const [activeSummary, setActiveSummary] = useState(null);
  const [cartera, setCartera] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [creditoParaGestion, setCreditoParaGestion] = useState(null);

  // Filtros
  const [search, setSearch] = useState('');
  const [tramoAtraso, setTramoAtraso] = useState('');
  const [modalidad, setModalidad] = useState('');
  const [frecuencia, setFrecuencia] = useState('');
  const [conPromesa, setConPromesa] = useState('');
  const [conGestion, setConGestion] = useState('');
  const [ordenar, setOrdenar] = useState('prioridad_desc');
  const [quickTab, setQuickTab] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const effectiveTramo = quickTab || tramoAtraso;
      const [resKpis, resCartera] = await Promise.all([
        cobranzaAPI.resumen(),
        cobranzaAPI.listar({
          page,
          limit,
          search: search || undefined,
          tramoAtraso: effectiveTramo || undefined,
          modalidad: modalidad || undefined,
          frecuencia: frecuencia || undefined,
          conPromesa: conPromesa === '' ? undefined : conPromesa,
          conGestion: conGestion === '' ? undefined : conGestion,
          ordenar
        })
      ]);

      setResumen(resKpis.data);
      setCartera(resCartera.data.creditos || []);
      setPagination(resCartera.data.pagination || { page: 1, pageSize: limit, total: 0, totalPages: 1 });
      setBucketSummary(resCartera.data.bucketSummary || null);
      setActiveSummary(resCartera.data.activeSummary || null);
    } catch (e) {
      setError(describirError(e).mensaje);
    } finally {
      setCargando(false);
    }
  }, [page, limit, search, tramoAtraso, modalidad, frecuencia, conPromesa, conGestion, ordenar, quickTab]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleSearchChange = (val) => {
    setSearch(val);
    setPage(1);
  };

  const handleQuickTabChange = (tabId) => {
    setQuickTab(tabId);
    setTramoAtraso(tabId);
    setPage(1);
  };

  const limpiarFiltros = () => {
    setSearch('');
    setTramoAtraso('');
    setModalidad('');
    setFrecuencia('');
    setConPromesa('');
    setConGestion('');
    setOrdenar('prioridad_desc');
    setQuickTab('');
    setPage(1);
  };

  const getPrioridadBadge = (prioridad, diasAtraso) => {
    if (prioridad === 'RECUPERACION' || diasAtraso > 90) {
      return <span className="badge badge-danger" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>RECUPERACIÓN</span>;
    }
    if (prioridad === 'RIESGO' || diasAtraso >= 31) {
      return <span className="badge badge-warning" style={{ backgroundColor: '#fff7ed', color: '#c2410c', border: '1px solid #ffedd5', fontWeight: '700', whiteSpace: 'nowrap' }}>RIESGO</span>;
    }
    if (prioridad === 'PREVENTIVA' || (diasAtraso >= 1 && diasAtraso <= 30)) {
      return <span className="badge" style={{ backgroundColor: '#fef9c3', color: '#854d0e', border: '1px solid #fef08a', fontWeight: '700', whiteSpace: 'nowrap' }}>MORA TEMPRANA</span>;
    }
    return <span className="badge badge-success" style={{ fontWeight: '700', whiteSpace: 'nowrap' }}>AL CORRIENTE</span>;
  };

  const getModalidadBadge = (tipoCredito) => {
    const tipo = (tipoCredito || 'INDIVIDUAL').toUpperCase();
    if (tipo === 'COLOCADORA') {
      return <span className="badge" style={{ backgroundColor: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe', fontSize: '0.72rem', fontWeight: '700', whiteSpace: 'nowrap' }}>COLOCADORA</span>;
    }
    if (tipo === 'GRUPAL') {
      return <span className="badge" style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontSize: '0.72rem', fontWeight: '700', whiteSpace: 'nowrap' }}>GRUPAL</span>;
    }
    return <span className="badge" style={{ backgroundColor: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', fontSize: '0.72rem', fontWeight: '700', whiteSpace: 'nowrap' }}>PERSONAL</span>;
  };

  const isAllCases = !quickTab && !tramoAtraso;
  const isAlCorriente = quickTab === '0' || tramoAtraso === '0' || tramoAtraso === 'AL_CORRIENTE' || tramoAtraso === 'POR_VENCER';

  return (
    <div className="cartera-container" style={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      {/* 1. ENCABEZADO */}
      <div className="cartera-header" style={{ marginBottom: '1rem' }}>
        <div>
          <div className="cartera-badge-env">
            <span className="cartera-badge-dot" />
            <span>{reviewMode ? 'BANDEJA OPERATIVA DE RECUPERACIÓN · MODO REVISIÓN' : 'BANDEJA OPERATIVA DE RECUPERACIÓN · PRODUCCIÓN'}</span>
          </div>
          <h1 className="cartera-title" style={{ margin: '0.25rem 0' }}>GESTIÓN DE COBRANZA</h1>
          <p className="cartera-subtitle" style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
            Bandeja operativa para priorizar recuperación, seguimiento y compromisos de pago.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={cargarDatos}
            disabled={cargando}
            title="Actualizar bandeja"
          >
            <RefreshCw size={15} className={cargando ? 'spin' : ''} />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* 2. KPIS DINÁMICOS CONTEXTUALES */}
      <div
        className="cartera-kpi-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.85rem',
          marginBottom: '1rem',
          width: '100%',
          minWidth: 0
        }}
      >
        {isAllCases ? (
          /* VISTA GLOBAL: TODOS LOS CASOS */
          <>
            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #ef4444', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#b91c1c', fontSize: '0.75rem', fontWeight: '700' }}>SALDO VENCIDO REAL</div>
              <div className="cartera-kpi-value font-mono text-danger" style={{ fontSize: '1.45rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {formatoMoneda(resumen?.saldoVencidoReal || bucketSummary?.todos?.monto || 0)}
              </div>
              <div className="cartera-kpi-sub text-danger" style={{ color: '#dc2626', fontSize: '0.75rem' }}>
                Total exigible en mora
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #ea580c', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#c2410c', fontSize: '0.75rem', fontWeight: '700' }}>CRÉDITOS VENCIDOS</div>
              <div className="cartera-kpi-value font-mono" style={{ color: '#c2410c', fontSize: '1.45rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {resumen?.creditosVencidos ?? bucketSummary?.todos?.creditos ?? '—'}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#ea580c', fontSize: '0.75rem' }}>
                Con saldo vencido &gt; $0
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #d97706', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#b45309', fontSize: '0.75rem', fontWeight: '700' }}>CLIENTES CON ATRASO</div>
              <div className="cartera-kpi-value font-mono" style={{ color: '#b45309', fontSize: '1.45rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {resumen?.clientesConAtraso ?? '—'}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#d97706', fontSize: '0.75rem' }}>
                Titulares únicos con mora
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #eab308', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#854d0e', fontSize: '0.75rem', fontWeight: '700' }}>MORA TEMPRANA (1–30 D)</div>
              <div className="cartera-kpi-value font-mono" style={{ color: '#854d0e', fontSize: '1.35rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {formatoMoneda(bucketSummary?.moraTemprana?.monto || resumen?.cartera1_30?.monto || 0)}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#a16207', fontSize: '0.75rem' }}>
                {bucketSummary?.moraTemprana?.creditos ?? resumen?.cartera1_30?.creditos ?? 0} créditos en mora
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #f97316', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#c2410c', fontSize: '0.75rem', fontWeight: '700' }}>RIESGO (31–90 D)</div>
              <div className="cartera-kpi-value font-mono" style={{ color: '#c2410c', fontSize: '1.35rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {formatoMoneda(bucketSummary?.riesgo?.monto || resumen?.cartera31_90?.monto || 0)}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#ea580c', fontSize: '0.75rem' }}>
                {bucketSummary?.riesgo?.creditos ?? resumen?.cartera31_90?.creditos ?? 0} créditos en riesgo
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #dc2626', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#991b1b', fontSize: '0.75rem', fontWeight: '700' }}>RECUPERACIÓN (91+ D)</div>
              <div className="cartera-kpi-value font-mono text-danger" style={{ fontSize: '1.35rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {formatoMoneda(bucketSummary?.recuperacion?.monto || resumen?.cartera91Plus?.monto || 0)}
              </div>
              <div className="cartera-kpi-sub text-danger" style={{ fontSize: '0.75rem' }}>
                {bucketSummary?.recuperacion?.creditos ?? resumen?.cartera91Plus?.creditos ?? 0} créditos críticos
              </div>
            </div>
          </>
        ) : isAlCorriente ? (
          /* VISTA CONTEXTUAL: AL CORRIENTE / VENCE HOY */
          <>
            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #10b981', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#047857', fontSize: '0.75rem', fontWeight: '700' }}>SALDO CONTRACTUAL</div>
              <div className="cartera-kpi-value font-mono text-emerald-700" style={{ fontSize: '1.45rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {formatoMoneda(activeSummary?.saldoActual || 0)}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#059669', fontSize: '0.75rem' }}>
                Cartera al corriente sin mora
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #10b981', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#047857', fontSize: '0.75rem', fontWeight: '700' }}>SALDO VENCIDO</div>
              <div className="cartera-kpi-value font-mono text-emerald-700" style={{ fontSize: '1.45rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {formatoMoneda(activeSummary?.saldoVencidoReal || 0)}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#059669', fontSize: '0.75rem' }}>
                Sin morosidad acumulada
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #0284c7', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#0369a1', fontSize: '0.75rem', fontWeight: '700' }}>CRÉDITOS</div>
              <div className="cartera-kpi-value font-mono" style={{ color: '#0369a1', fontSize: '1.45rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {activeSummary?.totalCreditos ?? 0}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#0284c7', fontSize: '0.75rem' }}>
                Créditos al corriente
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #6366f1', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#4338ca', fontSize: '0.75rem', fontWeight: '700' }}>CLIENTES</div>
              <div className="cartera-kpi-value font-mono" style={{ color: '#4338ca', fontSize: '1.45rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {activeSummary?.totalClientes ?? 0}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#6366f1', fontSize: '0.75rem' }}>
                Titulares sin atraso
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #0f766e', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#0f766e', fontSize: '0.75rem', fontWeight: '700' }}>CUOTA / EXIGIBLE HOY</div>
              <div className="cartera-kpi-value font-mono" style={{ color: '#0f766e', fontSize: '1.35rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {formatoMoneda(activeSummary?.cuotaExigible || 0)}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#0f766e', fontSize: '0.75rem' }}>
                Cuota programada activa
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #8b5cf6', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#6d28d9', fontSize: '0.75rem', fontWeight: '700' }}>PROMESAS VIGENTES</div>
              <div className="cartera-kpi-value font-mono" style={{ color: '#6d28d9', fontSize: '1.35rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {activeSummary?.promesasVigentes ?? 0}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#8b5cf6', fontSize: '0.75rem' }}>
                Compromisos activos
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #64748b', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#334155', fontSize: '0.75rem', fontWeight: '700' }}>SIN GESTIÓN</div>
              <div className="cartera-kpi-value font-mono" style={{ color: '#334155', fontSize: '1.35rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {activeSummary?.sinGestion ?? 0}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#64748b', fontSize: '0.75rem' }}>
                Sin contacto previo
              </div>
            </div>
          </>
        ) : (
          /* VISTA CONTEXTUAL: MORA TEMPRANA / RIESGO / RECUPERACIÓN */
          <>
            <div className="cartera-kpi-card is-brand" style={{ borderLeft: '5px solid #d97706', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#b45309', fontSize: '0.75rem', fontWeight: '700' }}>SALDO EN RIESGO</div>
              <div className="cartera-kpi-value font-mono" style={{ color: '#b45309', fontSize: '1.45rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {formatoMoneda(activeSummary?.saldoActual || activeSummary?.saldoEnRiesgo || 0)}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#d97706', fontSize: '0.75rem' }}>
                Saldo contractual total del segmento
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #ef4444', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#b91c1c', fontSize: '0.75rem', fontWeight: '700' }}>SALDO VENCIDO</div>
              <div className="cartera-kpi-value font-mono text-danger" style={{ fontSize: '1.45rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {formatoMoneda(activeSummary?.saldoVencidoReal || 0)}
              </div>
              <div className="cartera-kpi-sub text-danger" style={{ fontSize: '0.75rem' }}>
                Vencido para regularizar
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #ea580c', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#c2410c', fontSize: '0.75rem', fontWeight: '700' }}>CRÉDITOS</div>
              <div className="cartera-kpi-value font-mono" style={{ color: '#c2410c', fontSize: '1.45rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {activeSummary?.totalCreditos ?? 0}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#ea580c', fontSize: '0.75rem' }}>
                En el segmento activo
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #6366f1', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#4338ca', fontSize: '0.75rem', fontWeight: '700' }}>CLIENTES</div>
              <div className="cartera-kpi-value font-mono" style={{ color: '#4338ca', fontSize: '1.45rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {activeSummary?.totalClientes ?? 0}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#6366f1', fontSize: '0.75rem' }}>
                Titulares en el segmento
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #0284c7', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#0369a1', fontSize: '0.75rem', fontWeight: '700' }}>IMPORTE EXIGIBLE MÍNIMO</div>
              <div className="cartera-kpi-value font-mono text-primary" style={{ fontSize: '1.35rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {formatoMoneda(activeSummary?.cuotaExigible || 0)}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#0284c7', fontSize: '0.75rem' }}>
                Cuota más antigua en mora
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #10b981', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#047857', fontSize: '0.75rem', fontWeight: '700' }}>PROMESAS VIGENTES</div>
              <div className="cartera-kpi-value font-mono text-emerald-700" style={{ fontSize: '1.35rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {activeSummary?.promesasVigentes ?? 0}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#059669', fontSize: '0.75rem' }}>
                Compromisos activos
              </div>
            </div>

            <div className="cartera-kpi-card" style={{ borderLeft: '5px solid #64748b', padding: '0.85rem 1rem' }}>
              <div className="cartera-kpi-label" style={{ color: '#334155', fontSize: '0.75rem', fontWeight: '700' }}>SIN GESTIÓN</div>
              <div className="cartera-kpi-value font-mono" style={{ color: '#334155', fontSize: '1.35rem', fontWeight: '800', whiteSpace: 'nowrap', margin: '0.2rem 0' }}>
                {activeSummary?.sinGestion ?? 0}
              </div>
              <div className="cartera-kpi-sub" style={{ color: '#64748b', fontSize: '0.75rem' }}>
                Sin contacto previo
              </div>
            </div>
          </>
        )}
      </div>

      {/* 3. TABS RÁPIDOS DE BUCKETS CON TOTALES CALCULADOS */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', width: '100%', minWidth: 0 }}>
        {QUICK_TABS.map((t) => {
          const isSelected = quickTab === t.id;
          const bucketData = bucketSummary?.[t.key];
          const count = bucketData?.creditos ?? (t.id === '' ? resumen?.creditosVencidos : 0);
          const monto = bucketData?.monto ? formatoMoneda(bucketData.monto) : (t.id === '' ? formatoMoneda(resumen?.saldoVencidoReal || 0) : '$0.00');

          return (
            <button
              key={t.id}
              type="button"
              onClick={() => handleQuickTabChange(t.id)}
              style={{
                padding: '0.5rem 0.85rem',
                borderRadius: '0.45rem',
                fontSize: '0.8rem',
                fontWeight: '600',
                border: isSelected ? '2px solid #2563eb' : '1px solid #cbd5e1',
                backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                color: isSelected ? '#1d4ed8' : '#334155',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '0.15rem',
                boxShadow: isSelected ? '0 1px 3px rgba(37,99,235,0.12)' : 'none',
                flex: '1 1 auto',
                minWidth: '150px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', width: '100%', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '700', fontSize: '0.82rem' }}>{t.label}</span>
                {t.sub && <span style={{ fontSize: '0.68rem', opacity: 0.8, color: isSelected ? '#1d4ed8' : '#64748b' }}>{t.sub}</span>}
              </div>
              <div style={{ fontSize: '0.74rem', color: isSelected ? '#1e40af' : '#64748b', fontWeight: '500', whiteSpace: 'nowrap' }}>
                <span style={{ fontWeight: '700' }}>{count ?? 0}</span> {t.id === '' ? 'vencidos' : 'créditos'} · <span className="font-mono" style={{ fontWeight: '700' }}>{monto}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* 4. FILTROS SERVER-SIDE */}
      <div className="cobranza-filters-card" style={{ marginBottom: '1.25rem', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
        <div className="filters-header">
          <div className="filters-title">
            <Filter size={16} />
            <span>Filtros Operativos de Cobranza</span>
          </div>
          <div className="filters-actions">
            {(search || tramoAtraso || modalidad || frecuencia || conPromesa || conGestion || ordenar !== 'prioridad_desc') && (
              <button type="button" className="btn-text" onClick={limpiarFiltros} style={{ fontSize: '0.8rem', color: '#2563eb', cursor: 'pointer', background: 'none', border: 'none' }}>
                Limpiar filtros
              </button>
            )}
          </div>
        </div>

        <div className="cobranza-filters-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem', width: '100%' }}>
          {/* Búsqueda */}
          <div className="filter-item cobranza-search" style={{ gridColumn: 'span 2' }}>
            <label className="filter-label" htmlFor="busqueda">Buscar</label>
            <div className="search-box">
              <Search className="search-icon" size={16} />
              <input
                id="busqueda"
                type="search"
                placeholder="Cliente, folio crédito, teléfono, grupo o colocadora..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              {search && (
                <button type="button" className="search-clear" onClick={() => handleSearchChange('')}>
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Tramo de atraso */}
          <div className="filter-item">
            <label className="filter-label" htmlFor="tramoAtraso">Tramo de Atraso</label>
            <select
              id="tramoAtraso"
              className="form-control form-select"
              value={tramoAtraso}
              onChange={(e) => {
                setTramoAtraso(e.target.value);
                setQuickTab(e.target.value);
                setPage(1);
              }}
            >
              {TRAMOS_ATRASO.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Modalidad */}
          <div className="filter-item">
            <label className="filter-label" htmlFor="modalidad">Modalidad</label>
            <select
              id="modalidad"
              className="form-control form-select"
              value={modalidad}
              onChange={(e) => {
                setModalidad(e.target.value);
                setPage(1);
              }}
            >
              {MODALIDADES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Frecuencia */}
          <div className="filter-item">
            <label className="filter-label" htmlFor="frecuencia">Frecuencia</label>
            <select
              id="frecuencia"
              className="form-control form-select"
              value={frecuencia}
              onChange={(e) => {
                setFrecuencia(e.target.value);
                setPage(1);
              }}
            >
              {FRECUENCIAS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Promesa */}
          <div className="filter-item">
            <label className="filter-label" htmlFor="conPromesa">Promesa de Pago</label>
            <select
              id="conPromesa"
              className="form-control form-select"
              value={conPromesa}
              onChange={(e) => {
                setConPromesa(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todas</option>
              <option value="true">Con promesa vigente</option>
              <option value="false">Sin promesa</option>
            </select>
          </div>

          {/* Gestión */}
          <div className="filter-item">
            <label className="filter-label" htmlFor="conGestion">Historial de Gestión</label>
            <select
              id="conGestion"
              className="form-control form-select"
              value={conGestion}
              onChange={(e) => {
                setConGestion(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todas</option>
              <option value="true">Con gestión previa</option>
              <option value="false">Sin gestión registrada</option>
            </select>
          </div>

          {/* Ordenar */}
          <div className="filter-item">
            <label className="filter-label" htmlFor="ordenar">Ordenar por</label>
            <select
              id="ordenar"
              className="form-control form-select"
              value={ordenar}
              onChange={(e) => {
                setOrdenar(e.target.value);
                setPage(1);
              }}
            >
              {OPCIONES_ORDEN.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* 5. BANDEJA OPERATIVA (TABLA DESKTOP 9 COLUMNAS · FIXED LAYOUT) */}
      <div className="cobranza-tabla-wrapper">
        <table className="cobranza-tabla-fixed">
          <thead>
            <tr>
              <th style={{ width: '20%' }}>ENTIDAD / CLIENTE</th>
              <th style={{ width: '11%' }}>CRÉDITO</th>
              <th className="text-right" style={{ width: '10%', color: '#b91c1c' }}>SALDO VENCIDO</th>
              <th className="text-right" style={{ width: '12%', color: '#1d4ed8' }}>PAGO / CUOTA EXIGIBLE</th>
              <th className="text-center" style={{ width: '7%' }}>DÍAS ATRASO</th>
              <th style={{ width: '11%' }}>PROMESA</th>
              <th style={{ width: '10%' }}>ÚLTIMA GESTIÓN</th>
              <th style={{ width: '10%' }}>PRÓXIMA ACCIÓN</th>
              <th className="text-right" style={{ width: '9%' }}>ACCIÓN</th>
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr>
                <td colSpan="9" className="text-center py-6 text-muted" style={{ padding: '2.5rem', textAlign: 'center' }}>
                  <RefreshCw size={20} className="spin" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '8px' }} />
                  Cargando bandeja de cobranza…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="9" className="text-center py-6 text-danger" style={{ padding: '2.5rem', textAlign: 'center' }}>
                  <AlertTriangle size={20} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '8px' }} />
                  {error}
                </td>
              </tr>
            ) : cartera.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-6 text-muted" style={{ padding: '2.5rem', textAlign: 'center' }}>
                  No se encontraron créditos que coincidan con los criterios seleccionados.
                </td>
              </tr>
            ) : (
              cartera.map((c) => {
                const nombreAgrupacion = c.agrupacion ? limpiarNombreGrupo(c.agrupacion.nombre).nombre : null;
                const saldoVencidoNum = Number(c.saldoVencido || 0);
                const tipo = (c.tipoCredito || 'INDIVIDUAL').toUpperCase();
                const esColocadora = tipo === 'COLOCADORA';
                const esGrupal = tipo === 'GRUPAL';

                return (
                  <tr
                    key={c.creditoId}
                    className="fila-clicable"
                    onClick={() => navigate(`/creditos/${c.creditoId}?tab=cobranza`)}
                    title={`Abrir expediente de cobranza para crédito ${c.folio}`}
                  >
                    {/* 1. ENTIDAD / CLIENTE (CON BADGE DE PRIORIDAD INTEGRADO) */}
                    <td style={{ verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: 0 }}>
                        <div style={{ display: 'inline-flex' }}>
                          {getPrioridadBadge(c.prioridad, c.diasAtraso)}
                        </div>
                        {esColocadora ? (
                          <div>
                            {/* Padre: Colocadora */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
                              <Building2 size={13} style={{ color: '#7c3aed', flexShrink: 0 }} />
                              {c.agrupacion?.id ? (
                                <button
                                  type="button"
                                  className="btn-link"
                                  style={{ fontWeight: '700', color: '#6d28d9', fontSize: '0.82rem', textAlign: 'left', padding: 0, background: 'none', border: 'none', cursor: 'pointer', wordBreak: 'break-word' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/colocadoras/${c.agrupacion.id}`);
                                  }}
                                  title="Ver expediente de la Colocadora"
                                >
                                  {nombreAgrupacion || c.agrupacion.nombre}
                                </button>
                              ) : (
                                <span style={{ fontWeight: '700', color: '#6d28d9', fontSize: '0.82rem', wordBreak: 'break-word' }}>
                                  {nombreAgrupacion || 'Colocadora'}
                                </span>
                              )}
                              <span className="font-mono text-xs" style={{ color: '#7c3aed', opacity: 0.85 }}>({c.agrupacion?.folio || 'CO-000000'})</span>
                            </div>
                            {/* Hijo: Cliente */}
                            <div style={{ paddingLeft: '0.6rem', marginTop: '0.15rem' }}>
                              <div className="font-medium text-slate-800" style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'flex-start', gap: '0.2rem' }}>
                                <span style={{ color: '#94a3b8', fontWeight: 'bold', flexShrink: 0 }}>└─</span>
                                <span style={{ wordBreak: 'break-word', lineHeight: '1.25' }}>
                                  {c.cliente?.nombre || '—'}
                                </span>
                              </div>
                              <div className="text-xs text-muted font-mono" style={{ paddingLeft: '0.6rem', marginTop: '0.1rem', wordBreak: 'break-word' }}>
                                {c.cliente?.folio || '—'} {formatoTelefono(c.cliente?.telefono) !== 'Sin teléfono registrado' ? `· 📞 ${formatoTelefono(c.cliente?.telefono)}` : ''}
                              </div>
                            </div>
                          </div>
                        ) : esGrupal ? (
                          <div>
                            {/* Padre: Grupo */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
                              <Building2 size={13} style={{ color: '#2563eb', flexShrink: 0 }} />
                              {c.agrupacion?.id ? (
                                <button
                                  type="button"
                                  className="btn-link"
                                  style={{ fontWeight: '700', color: '#1d4ed8', fontSize: '0.82rem', textAlign: 'left', padding: 0, background: 'none', border: 'none', cursor: 'pointer', wordBreak: 'break-word' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/grupos/${c.agrupacion.id}`);
                                  }}
                                  title="Ver expediente del Grupo"
                                >
                                  {nombreAgrupacion || c.agrupacion.nombre}
                                </button>
                              ) : (
                                <span style={{ fontWeight: '700', color: '#1d4ed8', fontSize: '0.82rem', wordBreak: 'break-word' }}>
                                  {nombreAgrupacion || 'Grupo'}
                                </span>
                              )}
                              <span className="font-mono text-xs text-muted">({c.agrupacion?.folio || 'GR-000000'})</span>
                            </div>
                            {/* Hijo: Integrante */}
                            <div style={{ paddingLeft: '0.6rem', marginTop: '0.15rem' }}>
                              <div className="font-medium text-slate-800" style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'flex-start', gap: '0.2rem' }}>
                                <span style={{ color: '#94a3b8', fontWeight: 'bold', flexShrink: 0 }}>└─</span>
                                <span style={{ wordBreak: 'break-word', lineHeight: '1.25' }}>
                                  {c.cliente?.nombre || '—'}
                                </span>
                              </div>
                              <div className="text-xs text-muted font-mono" style={{ paddingLeft: '0.6rem', marginTop: '0.1rem', wordBreak: 'break-word' }}>
                                {c.cliente?.folio || '—'} {formatoTelefono(c.cliente?.telefono) !== 'Sin teléfono registrado' ? `· 📞 ${formatoTelefono(c.cliente?.telefono)}` : ''}
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* Personal */
                          <div>
                            <div className="font-semibold text-slate-800" style={{ fontSize: '0.84rem', wordBreak: 'break-word', lineHeight: '1.25' }}>
                              {c.cliente?.nombre || '—'}
                            </div>
                            <div className="text-xs text-muted font-mono" style={{ marginTop: '0.1rem', wordBreak: 'break-word' }}>
                              {c.cliente?.folio || '—'} {formatoTelefono(c.cliente?.telefono) !== 'Sin teléfono registrado' ? `· 📞 ${formatoTelefono(c.cliente?.telefono)}` : ''}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* 2. CRÉDITO (FOLIO + MODALIDAD + FRECUENCIA) */}
                    <td style={{ verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
                          <span className="font-mono font-bold" style={{ fontSize: '0.84rem', color: '#0f172a', whiteSpace: 'nowrap' }}>
                            {c.folio}
                          </span>
                          {getModalidadBadge(c.tipoCredito)}
                        </div>
                        <div className="text-xs text-muted" style={{ fontSize: '0.72rem' }}>
                          {c.producto?.frecuencia || '—'}
                        </div>
                      </div>
                    </td>

                    {/* 3. SALDO VENCIDO (NOWRAP, NO TRUNCATION) */}
                    <td className="text-right font-mono font-bold" style={{ verticalAlign: 'middle', fontSize: '0.88rem', color: saldoVencidoNum > 0 ? '#dc2626' : '#16a34a', whiteSpace: 'nowrap' }}>
                      {formatoMoneda(c.saldoVencido)}
                    </td>

                    {/* 4. PAGO / CUOTA EXIGIBLE (NOWRAP) */}
                    <td className="text-right font-mono" style={{ verticalAlign: 'middle', fontSize: '0.84rem' }}>
                      {c.cuotaExigible ? (
                        <div>
                          <span className="font-bold text-primary" style={{ whiteSpace: 'nowrap' }}>{formatoMoneda(c.cuotaExigible.importe)}</span>
                          <div className="text-xs text-muted" style={{ fontSize: '0.72rem', whiteSpace: 'normal', lineHeight: '1.2' }}>
                            Cuota #{c.cuotaExigible.numeroPago} ({fechaCorta(c.cuotaExigible.fechaVencimiento)})
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted text-xs">—</span>
                      )}
                    </td>

                    {/* 5. DÍAS ATRASO */}
                    <td className="text-center font-mono" style={{ verticalAlign: 'middle' }}>
                      {c.diasAtraso > 0 ? (
                        <span className="badge badge-danger" style={{ fontWeight: '700', padding: '0.2rem 0.45rem', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                          {c.diasAtraso} d
                        </span>
                      ) : (
                        <span className="badge badge-success" style={{ fontWeight: '700', padding: '0.2rem 0.45rem', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                          0 d
                        </span>
                      )}
                    </td>

                    {/* 6. PROMESA */}
                    <td style={{ verticalAlign: 'middle' }}>
                      {c.promesaVigente ? (
                        <div style={{ fontSize: '0.78rem' }}>
                          <span className="font-bold text-emerald-700" style={{ whiteSpace: 'nowrap' }}>{formatoMoneda(c.promesaVigente.importePrometido)}</span>
                          <div className="text-xs text-slate-600" style={{ whiteSpace: 'nowrap' }}>{fechaCorta(c.promesaVigente.fechaPromesa)}</div>
                        </div>
                      ) : (
                        <span className="text-muted text-xs" style={{ whiteSpace: 'nowrap' }}>Sin promesa</span>
                      )}
                    </td>

                    {/* 7. ÚLTIMA GESTIÓN */}
                    <td style={{ verticalAlign: 'middle' }}>
                      {c.ultimaGestion ? (
                        <div style={{ fontSize: '0.78rem', minWidth: 0 }}>
                          <div className="font-semibold text-slate-700" style={{ whiteSpace: 'nowrap' }}>{fechaCorta(c.ultimaGestion.fechaGestion)}</div>
                          <div className="text-muted text-xs" style={{ wordBreak: 'break-word', lineHeight: '1.25' }}>{c.ultimaGestion.resultadoGestion}</div>
                        </div>
                      ) : (
                        <span className="text-muted text-xs" style={{ whiteSpace: 'nowrap' }}>Sin gestión</span>
                      )}
                    </td>

                    {/* 8. PRÓXIMA ACCIÓN */}
                    <td style={{ verticalAlign: 'middle' }}>
                      {c.proximaAccion ? (
                        <div style={{ fontSize: '0.78rem', minWidth: 0 }}>
                          <div className="font-semibold text-slate-700" style={{ whiteSpace: 'nowrap' }}>{fechaCorta(c.proximaAccion.fecha)}</div>
                          <div className="text-muted text-xs" style={{ wordBreak: 'break-word', lineHeight: '1.25' }}>{c.proximaAccion.tipo}</div>
                        </div>
                      ) : (
                        <span className="text-muted text-xs" style={{ whiteSpace: 'nowrap' }}>Sin agendar</span>
                      )}
                    </td>

                    {/* 9. ACCIÓN */}
                    <td className="text-right" style={{ verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          style={{ padding: '0.25rem 0.45rem', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.2rem', whiteSpace: 'nowrap' }}
                          onClick={() => navigate(`/creditos/${c.creditoId}?tab=cobranza`)}
                          title="Ver expediente de cobranza"
                        >
                          <Eye size={12} />
                          <span>Exp.</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          style={{ padding: '0.25rem 0.45rem', fontSize: '0.72rem', display: 'flex', alignItems: 'center' }}
                          onClick={() => setCreditoParaGestion(c)}
                          title="Registrar gestión / compromiso de pago"
                        >
                          <PhoneCall size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 6. PAGINACIÓN SERVER-SIDE */}
      {!cargando && pagination.total > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', padding: '0.5rem 0', flexWrap: 'wrap', gap: '0.75rem', width: '100%', minWidth: 0 }}>
          <div className="text-muted text-sm">
            Mostrando {((page - 1) * limit) + 1} – {Math.min(page * limit, pagination.total)} de {pagination.total} créditos
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
              <span className="text-muted">Por página:</span>
              <select
                className="form-control form-select form-select-sm"
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                style={{ width: '70px', padding: '0.2rem 0.5rem' }}
              >
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '0.35rem' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                title="Página anterior"
              >
                <ChevronLeft size={16} />
              </button>
              <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.85rem', fontWeight: '600' }}>
                {page} / {pagination.totalPages}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={page >= pagination.totalPages}
                title="Página siguiente"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 7. MODAL REGISTRAR GESTIÓN */}
      {creditoParaGestion && (
        <RegistrarGestionModal
          credito={creditoParaGestion}
          saldoActual={creditoParaGestion.saldoActual}
          saldoVencido={creditoParaGestion.saldoVencido}
          diasAtraso={creditoParaGestion.diasAtraso}
          hayPromesaVigente={!!creditoParaGestion.promesaVigente}
          onClose={() => setCreditoParaGestion(null)}
          onRegistrada={async () => {
            setCreditoParaGestion(null);
            await cargarDatos();
            toast.exito('Gestión registrada correctamente.');
          }}
        />
      )}
    </div>
  );
}
